import type { KeyPairJwk, Locale, PeerRecord, SkyChatApi } from '@skychat/shared-core';

const SERVICE_WORKER_URL = '/service-worker.js';
const DB_NAME = 'SkyChatDB';
const DB_VERSION = 7;
const STORES = {
  keys: 'keys',
  friends: 'friends',
  decryptedMedia: 'decryptedMedia',
  apod: 'apod',
  pendingUploads: 'pendingUploads'
} as const;

export type WebPushPermission = NotificationPermission | 'unsupported';

export interface WebPushState {
  supported: boolean;
  permission: WebPushPermission;
  subscriptionActive: boolean;
  error?: string;
}

export interface ServiceWorkerSyncState {
  keypair?: KeyPairJwk | null;
  peers?: PeerRecord[];
  locale?: Locale;
  previewsEnabled?: boolean;
}

interface VapidResponse {
  publicKey?: unknown;
  data?: { publicKey?: unknown };
  error?: string;
  ok?: boolean;
}

interface ServiceWorkerBridgeOptions {
  getKeys(): KeyPairJwk | null;
  getFriends(): PeerRecord[];
  onNavigateToChat(peerId: string): void;
}

function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

function currentPermission(): WebPushPermission {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return buffer;
}

async function openServiceWorkerDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return null;
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => resolve(null);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORES.keys)) {
        db.createObjectStore(STORES.keys, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.friends)) {
        db.createObjectStore(STORES.friends, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.decryptedMedia)) {
        db.createObjectStore(STORES.decryptedMedia, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.apod)) {
        db.createObjectStore(STORES.apod, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.pendingUploads)) {
        db.createObjectStore(STORES.pendingUploads, { keyPath: 'id' });
      }
    };
  });
}

function runStoreTransaction(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    operation(transaction.objectStore(storeName));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

async function saveServiceWorkerCache(state: ServiceWorkerSyncState): Promise<void> {
  const db = await openServiceWorkerDb();
  if (!db) return;
  try {
    if (state.keypair?.myPrivJwk || state.keypair?.myPubJwk) {
      await runStoreTransaction(db, STORES.keys, 'readwrite', (store) => {
        store.put({
          id: 'my-keypair',
          myPubJwk: state.keypair?.myPubJwk || null,
          myPrivJwk: state.keypair?.myPrivJwk || null
        });
      });
    }

    if (state.locale) {
      await runStoreTransaction(db, STORES.keys, 'readwrite', (store) => {
        store.put({ id: 'current-lang', lang: state.locale });
      });
    }

    if (typeof state.previewsEnabled === 'boolean') {
      await runStoreTransaction(db, STORES.keys, 'readwrite', (store) => {
        store.put({ id: 'notification-prefs', previewsEnabled: state.previewsEnabled });
      });
    }

    if (state.peers) {
      await runStoreTransaction(db, STORES.friends, 'readwrite', (store) => {
        store.clear();
        state.peers?.forEach((peer) => {
          if (peer?.id) store.put(peer);
        });
      });
    }
  } finally {
    db.close();
  }
}

async function getExistingRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.getRegistration().then((registration) => registration ?? null).catch(() => null);
}

async function getOrRegisterServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await getExistingRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register(SERVICE_WORKER_URL);
}

function postToServiceWorker(message: unknown): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  void getExistingRegistration().then((registration) => {
    const worker = navigator.serviceWorker.controller
      || registration?.active
      || registration?.waiting
      || registration?.installing;
    worker?.postMessage(message);
  });
}

export async function syncServiceWorkerState(state: ServiceWorkerSyncState): Promise<void> {
  await saveServiceWorkerCache(state);
  if (state.keypair) {
    postToServiceWorker({ type: 'sync_keys', keys: state.keypair });
  }
  if (state.peers) {
    postToServiceWorker({ type: 'sync_friends', friends: state.peers });
  }
}

export async function getWebPushState(): Promise<WebPushState> {
  if (!pushSupported()) {
    return { supported: false, permission: 'unsupported', subscriptionActive: false };
  }
  const registration = await getExistingRegistration();
  const subscription = await registration?.pushManager.getSubscription().catch(() => null);
  return {
    supported: true,
    permission: Notification.permission,
    subscriptionActive: Boolean(subscription)
  };
}

async function ensurePushPermission(requestPermission: boolean): Promise<NotificationPermission> {
  if (Notification.permission === 'granted') return 'granted';
  if (!requestPermission) return Notification.permission;
  return Notification.requestPermission();
}

function subscriptionJson(subscription: PushSubscription): unknown {
  return typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
}

export async function enableWebPush(api: SkyChatApi, options: { requestPermission: boolean }): Promise<WebPushState> {
  if (!pushSupported()) {
    return { supported: false, permission: 'unsupported', subscriptionActive: false };
  }

  const registration = await getOrRegisterServiceWorker();
  const permission = await ensurePushPermission(options.requestPermission);
  if (permission !== 'granted') {
    return { supported: true, permission, subscriptionActive: false };
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const response = await api.getVapidKey() as VapidResponse;
    const publicKey = String(response.publicKey || response.data?.publicKey || '');
    if (!publicKey) {
      throw new Error(response.error || 'VAPID public key is unavailable');
    }
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(publicKey)
    });
  }

  const result = await api.subscribe(subscriptionJson(subscription));
  if (result.ok === false) {
    throw new Error(result.error || 'Push subscription failed');
  }

  return {
    supported: true,
    permission: 'granted',
    subscriptionActive: true
  };
}

export async function restoreWebPush(api: SkyChatApi): Promise<WebPushState> {
  return enableWebPush(api, { requestPermission: false });
}

export async function disableWebPush(api: SkyChatApi): Promise<WebPushState> {
  if (!pushSupported()) {
    return { supported: false, permission: 'unsupported', subscriptionActive: false };
  }

  const registration = await getExistingRegistration();
  const subscription = await registration?.pushManager.getSubscription().catch(() => null);
  if (subscription) {
    const result = await api.unsubscribe({ endpoint: subscription.endpoint });
    if (result.ok === false) {
      throw new Error(result.error || 'Push unsubscribe failed');
    }
    await subscription.unsubscribe();
  }

  return {
    supported: true,
    permission: Notification.permission,
    subscriptionActive: false
  };
}

export function bindServiceWorkerBridge(options: ServiceWorkerBridgeOptions): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};

  const listener = (event: MessageEvent<Record<string, unknown>>) => {
    const data = event.data || {};
    const source = event.source as ServiceWorker | null;
    const target = typeof source?.postMessage === 'function'
      ? source
      : navigator.serviceWorker.controller;

    if (data.type === 'request_getKeys') {
      target?.postMessage({
        type: 'getKeys_response',
        requestId: data.requestId,
        data: options.getKeys()
      });
      return;
    }

    if (data.type === 'request_getFriends') {
      target?.postMessage({
        type: 'getFriends_response',
        requestId: data.requestId,
        data: options.getFriends()
      });
      return;
    }

    if (data.type === 'NAVIGATE_TO_CHAT') {
      const peerId = String(data.peerId || '');
      if (peerId) options.onNavigateToChat(peerId);
    }
  };

  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}

export function webPushPermission(): WebPushPermission {
  return currentPermission();
}
